// ===========================================
// SERVIÇO DE PRÉ-CARREGAMENTO DE PÁGINAS
// ===========================================

import { cacheService } from './cacheService';
import api from './api';

interface PreloadConfig {
  enablePreload: boolean;
  preloadDelay: number; // Delay em ms antes de pré-carregar
  maxConcurrent: number; // Máximo de requisições simultâneas
}

class PreloadService {
  private config: PreloadConfig = {
    enablePreload: true,
    preloadDelay: 2000, // 2 segundos
    maxConcurrent: 3
  };

  private preloadQueue: Array<() => Promise<void>> = [];
  private activePreloads = 0;

  constructor() {
    // Iniciar processamento da fila
    this.processQueue();
  }

  // Configurar pré-carregamento
  setConfig(config: Partial<PreloadConfig>) {
    this.config = { ...this.config, ...config };
  }

  // Adicionar à fila de pré-carregamento
  private addToQueue(preloadFn: () => Promise<void>) {
    this.preloadQueue.push(preloadFn);
  }

  // Processar fila de pré-carregamento
  private async processQueue() {
    while (true) {
      if (this.preloadQueue.length > 0 && this.activePreloads < this.config.maxConcurrent) {
        const preloadFn = this.preloadQueue.shift();
        if (preloadFn) {
          this.activePreloads++;
          preloadFn().finally(() => {
            this.activePreloads--;
          });
        }
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  // Pré-carregar dados da empresa
  preloadEmpresa(empresaId: number) {
    if (!this.config.enablePreload) return;

    const key = `empresa_${empresaId}`;
    
    // Verificar se já está no cache (async)
    this.addToQueue(async () => {
      const cached = await cacheService.get(key);
      if (cached) return;
      try {
        // Usar rota pública para evitar problemas de autenticação
        const response = await api.get(`/api/empresa/${empresaId}/public`);
        cacheService.set(key, response.data, 10 * 60 * 1000); // 10 minutos
      } catch (error) {
        console.warn('Erro ao pré-carregar dados da empresa:', error);
      }
    });
  }

  // Pré-carregar menu de produtos
  preloadMenu(empresaId: number) {
    if (!this.config.enablePreload) return;

    const key = `menu_${empresaId}`;
    
    this.addToQueue(async () => {
      const cached = await cacheService.get(key);
      if (cached) return;
      try {
        const [produtosResponse, categoriasResponse] = await Promise.all([
          api.get(`/api/menu/${empresaId}`),
          api.get(`/api/categorias/${empresaId}`)
        ]);
        
        const menuData = {
          produtos: produtosResponse.data,
          categorias: categoriasResponse.data
        };
        
        cacheService.set(key, menuData, 5 * 60 * 1000); // 5 minutos
      } catch (error) {
        console.warn('Erro ao pré-carregar menu:', error);
      }
    });
  }

  // Pré-carregar dados do garçom
  preloadGarcomData(empresaId: number) {
    if (!this.config.enablePreload) return;

    const key = `garcom_data_${empresaId}`;
    
    this.addToQueue(async () => {
      const cached = await cacheService.get(key);
      if (cached) return;
      try {
        const [comandasResponse, menuResponse] = await Promise.all([
          api.get('/api/garcom/comandas'),
          api.get(`/api/garcom/menu/${empresaId}`)
        ]);
        
        const garcomData = {
          comandas: comandasResponse.data.comandas,
          menu: menuResponse.data
        };
        
        cacheService.set(key, garcomData, 2 * 60 * 1000); // 2 minutos
      } catch (error) {
        console.warn('Erro ao pré-carregar dados do garçom:', error);
      }
    });
  }

  // Pré-carregar pedidos
  preloadPedidos() {
    if (!this.config.enablePreload) return;

    const key = 'pedidos_delivery';
    
    this.addToQueue(async () => {
      const cached = await cacheService.get(key);
      if (cached) return;
      try {
        const response = await api.get('/api/pedidos');
        const pedidosDelivery = response.data.filter((pedido: any) => 
          pedido.tipo_pedido === 'entrega' || pedido.tipo_pedido === 'delivery'
        );
        
        cacheService.set(key, pedidosDelivery, 1 * 60 * 1000); // 1 minuto
      } catch (error) {
        console.warn('Erro ao pré-carregar pedidos:', error);
      }
    });
  }

  // Pré-carregar configurações
  preloadConfiguracoes() {
    if (!this.config.enablePreload) return;

    const key = 'configuracoes_empresa';
    
    this.addToQueue(async () => {
      const cached = await cacheService.get(key);
      if (cached) return;
      try {
        const response = await api.get('/api/empresas/1');
        cacheService.set(key, response.data, 15 * 60 * 1000); // 15 minutos
      } catch (error) {
        console.warn('Erro ao pré-carregar configurações:', error);
      }
    });
  }

  // Pré-carregar página específica
  preloadPage(page: string, empresaId?: number) {
    if (!this.config.enablePreload) return;

    setTimeout(() => {
      switch (page) {
        case 'menu-publico':
          if (empresaId) {
            this.preloadEmpresa(empresaId);
            this.preloadMenu(empresaId);
          }
          break;
        case 'dashboard':
          this.preloadConfiguracoes();
          this.preloadPedidos();
          break;
        case 'garcom-dashboard':
          if (empresaId) {
            this.preloadGarcomData(empresaId);
          }
          break;
        case 'garcom-comanda':
          if (empresaId) {
            this.preloadMenu(empresaId);
          }
          break;
      }
    }, this.config.preloadDelay);
  }

  // Limpar cache de pré-carregamento
  clearPreloadCache() {
    cacheService.invalidatePattern('^empresa_');
    cacheService.invalidatePattern('^menu_');
    cacheService.invalidatePattern('^garcom_data_');
    cacheService.invalidatePattern('^pedidos_');
    cacheService.invalidatePattern('^configuracoes_');
  }
}

// Instância singleton
export const preloadService = new PreloadService();

// ===========================================
// HOOK PARA PRÉ-CARREGAMENTO
// ===========================================

import { useEffect } from 'react';

export function usePreloadPage(page: string, empresaId?: number) {
  useEffect(() => {
    preloadService.preloadPage(page, empresaId);
  }, [page, empresaId]);
}

export default preloadService;
